import { SHOULD_SHOW_FEEDBACK_LIST_SUMMARY_VIEW } from '@modules/feedback/constants';
import {
  useIsFeedbackListRedesignEnabled,
  useIsFeedbackRedesignEnabled,
} from '@modules/feedbackV2/common/hooks';
import { BANNER_HEIGHT } from '@modules/infoBanner/constants';
import { useIsInfoBannerVisible } from '@modules/infoBanner/hooks';
import {
  AutoAwesomeOutlined,
  AutoAwesomeRounded,
  ChatBubbleOutlineRounded,
  ChatBubbleRounded,
  ChatRounded,
  SummarizeRounded,
} from '@mui/icons-material';
import {
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/system';
import Locker from 'lockr';
import { useCallback, useEffect, useRef, useState } from 'react';
import { trackFeedbackSummaryEvents } from '../FeedbackSummary/utils';

interface FeedbackSummaryGlobalToggleProps {
  isSummaryView: boolean;
  isDrawer?: boolean;
  showShrinkedButton?: boolean;
  onChange: (isSummaryView: boolean) => void;
}

// To add extra spacing on the top of the button
const EXTRA_HEIGHT = 8;
const DISTANCE_FROM_TOP = 20;

export const FeedbackSummaryGlobalToggle = ({
  isSummaryView,
  isDrawer,
  showShrinkedButton,
  onChange,
}: FeedbackSummaryGlobalToggleProps) => {
  const theme = useTheme();

  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const isFeedbackListRedesignEnabled = useIsFeedbackListRedesignEnabled();

  const [shouldShrinkButton, setShouldShrinkButton] = useState(false);
  const toggleRef = useRef<HTMLDivElement>(null);

  const isInfoBannerVisible = useIsInfoBannerVisible();
  const buttonDistanceFromTop =
    (isInfoBannerVisible ? BANNER_HEIGHT : DISTANCE_FROM_TOP) + EXTRA_HEIGHT;

  const handleChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, isSummaryView: boolean | null) => {
      if (isSummaryView !== null) {
        onChange(isSummaryView);
        Locker.set(SHOULD_SHOW_FEEDBACK_LIST_SUMMARY_VIEW, isSummaryView);
        trackFeedbackSummaryEvents({
          eventName: isSummaryView
            ? 'Feedback List - Summary Toggled On'
            : 'Feedback List - Summary Toggled Off',
          route: window.location.pathname,
          state: shouldShrinkButton ? 'Collapsed' : 'Expanded',
          toggleLocation: isDrawer ? 'Feedback Drawer' : 'Search List',
        });
      }
    },
    [isDrawer, shouldShrinkButton, onChange],
  );

  useEffect(() => {
    if (!isDrawer) {
      const handleScroll = () => {
        const currentDistance =
          toggleRef.current?.getBoundingClientRect().top ?? 0;
        setShouldShrinkButton(currentDistance <= buttonDistanceFromTop);
      };
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isDrawer, buttonDistanceFromTop]);

  const isToggleButtonShrinked = shouldShrinkButton || showShrinkedButton;

  const toggleButtonTextDisplay = isToggleButtonShrinked ? 'none' : 'inherit';
  const toggleButtonWidth = isToggleButtonShrinked ? 50 : 120;

  const renderFeedbackIcon = () => {
    if (!isFeedbackRedesignEnabled) {
      return <ChatRounded color="inherit" />;
    }

    return isSummaryView ? (
      <ChatBubbleOutlineRounded color="inherit" />
    ) : (
      <ChatBubbleRounded color="inherit" />
    );
  };

  const renderSummaryIcon = () => {
    if (!isFeedbackRedesignEnabled) {
      return <SummarizeRounded color="inherit" />;
    }

    return isSummaryView ? (
      <AutoAwesomeRounded color="inherit" />
    ) : (
      <AutoAwesomeOutlined color="inherit" />
    );
  };

  return (
    <Stack
      zIndex={1}
      width="max-content"
      position="sticky"
      top={buttonDistanceFromTop}
      sx={
        !isDrawer
          ? {
              '&&': {
                mb: '-55px',
                ml: 'auto',
              },
            }
          : {}
      }
      ref={toggleRef}
    >
      <Paper elevation={0} sx={{ borderRadius: '5px' }}>
        <ToggleButtonGroup
          exclusive
          onChange={handleChange}
          aria-label="Feedback Summary Global Toggle"
          sx={{
            height: '36.5px',
            '& .MuiToggleButton-root': { transition: 'width 0.3s ease-in' },
          }}
        >
          <Tooltip title={isToggleButtonShrinked ? 'Show Feedback' : ''}>
            <ToggleButton
              color="warning"
              selected={!isSummaryView}
              value={false}
              sx={{
                borderRadius: '5px',
                width: toggleButtonWidth,
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.feedback.label, 0.15),
                },
                '&.Mui-selected:hover': {
                  backgroundColor: alpha(theme.palette.feedback.label, 0.25),
                },
              }}
              disableRipple
            >
              <Stack
                direction="row"
                spacing={1}
                overflow="hidden"
                sx={{
                  color:
                    isFeedbackListRedesignEnabled && isSummaryView
                      ? 'text.secondary'
                      : 'feedback.label',
                }}
              >
                {renderFeedbackIcon()}
                <Typography
                  variant="subtitle2"
                  textTransform="none"
                  color="inherit"
                  sx={{
                    display: toggleButtonTextDisplay,
                  }}
                >
                  Feedback
                </Typography>
              </Stack>
            </ToggleButton>
          </Tooltip>
          <Tooltip title={isToggleButtonShrinked ? 'Show Summary' : ''}>
            <ToggleButton
              color="info"
              selected={isSummaryView}
              value={true}
              sx={{
                borderRadius: '5px',
                width: toggleButtonWidth,
              }}
              disableRipple
            >
              <Stack
                direction="row"
                spacing={1}
                overflow="hidden"
                sx={{
                  color:
                    isFeedbackListRedesignEnabled && !isSummaryView
                      ? 'text.secondary'
                      : 'info.main',
                }}
              >
                {renderSummaryIcon()}
                <Typography
                  variant="subtitle2"
                  textTransform="none"
                  color="inherit"
                  sx={{
                    display: toggleButtonTextDisplay,
                  }}
                >
                  Summary
                </Typography>
              </Stack>
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Paper>
    </Stack>
  );
};
